import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../auth/useAuth';
import { API_BASE_URL } from '../config';
import { FaChartBar } from 'react-icons/fa';
import { LiveGridSkeleton } from '../components/MatchCardSkeleton';
import './LivePage.css';

/* ─────────────────────────────────────────────
   Live Match Card — read-only, score in center
───────────────────────────────────────────── */
function TeamImg({ src, alt, size = 36 }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <div className="lmc-team-fallback" style={{ width: size, height: size }}>
        ⚽
      </div>
    );
  }
  return (
    <img
      src={src} alt={alt}
      width={size} height={size}
      className="lmc-team-logo"
      onError={() => setBroken(true)}
    />
  );
}

function LiveMatchCard({ match }) {
  const elapsed = match.minute ?? match.elapsed ?? null;
  const statusLabel = match.status === '1H' ? '1ère mi-temps'
    : match.status === '2H' ? '2ème mi-temps'
    : match.status === 'HT' ? 'Mi-temps'
    : 'LIVE';

  return (
    <div className="lmc">
      {/* Header — league + LIVE badge */}
      <div className="lmc-header">
        <div className="lmc-league">
          {match.league_logo && (
            <img src={match.league_logo} alt={match.league}
              width={14} height={14} className="lmc-league-logo"
              onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <span>{match.league}</span>
        </div>
        <div className="lmc-live-badge">
          <span className="lmc-live-dot" />
          {elapsed ? `${elapsed}'` : statusLabel}
        </div>
      </div>

      {/* Teams + score */}
      <div className="lmc-body">
        <div className="lmc-team">
          <TeamImg src={match.home_logo} alt={match.home_team} />
          <span className="lmc-team-name">{match.home_team}</span>
        </div>

        <div className="lmc-score">
          <span className="lmc-score-num">{match.home_score ?? 0}</span>
          <span className="lmc-score-sep">—</span>
          <span className="lmc-score-num">{match.away_score ?? 0}</span>
        </div>

        <div className="lmc-team lmc-team-right">
          <span className="lmc-team-name">{match.away_team}</span>
          <TeamImg src={match.away_logo} alt={match.away_team} />
        </div>
      </div>

      {/* Footer — read-only info */}
      <div className="lmc-footer">
        <span className="lmc-readonly"><FaChartBar size={11} /> Stats en direct</span>
        <span className="lmc-status-label">Cotes verrouillées</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Live statuses recognized as "in play"
───────────────────────────────────────────── */
const LIVE_STATUSES = ['LIVE', '1H', '2H', 'HT', 'live', '1h', '2h', 'ht'];

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
export default function LivePage() {
  const { accessToken } = useAuth();

  // All live fixtures — unfiltered
  const [allFixtures, setAllFixtures] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [lastUpdate, setLastUpdate]   = useState(null);

  // League filter — derived client-side
  const [activeLeague, setActiveLeague] = useState(null); // null = "Toutes"

  // Derive unique leagues from fetched fixtures
  const leagues = useMemo(() => {
    const seen = new Map();
    allFixtures.forEach((m) => {
      if (m.league && !seen.has(m.league)) {
        seen.set(m.league, { league: m.league, league_logo: m.league_logo ?? null });
      }
    });
    return Array.from(seen.values());
  }, [allFixtures]);

  // Apply client-side league filter
  const fixtures = useMemo(() => {
    if (!activeLeague) return allFixtures;
    return allFixtures.filter((m) => m.league === activeLeague);
  }, [allFixtures, activeLeague]);

  const count = fixtures.length;

  /* ── Fetch live fixtures — all statuses ── */
  const fetchLive = useCallback(async () => {
    try {
      // Fetch without status filter, then filter client-side
      // This way we catch all live statuses (1H, 2H, HT, LIVE)
      // regardless of what the API accepts as status param
      const res = await fetch(`${API_BASE_URL}/api/v1/fixtures`, {
        credentials: 'include',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });

      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();

      const raw = Array.isArray(data) ? data : (data.fixtures ?? []);

      // Keep only matches with a live status
      const list = raw.filter((m) => LIVE_STATUSES.includes(m.status));

      setAllFixtures(list);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  /* ── Initial load + 60s polling ── */
  useEffect(() => {
    setLoading(true);
    fetchLive();
    const interval = setInterval(fetchLive, 30_000);
    return () => clearInterval(interval);
  }, [fetchLive]);

  /* ── Ticker text ── */
  const tickerItems = allFixtures.map(
    (m) => `${m.home_team} ${m.home_score ?? 0} — ${m.away_score ?? 0} ${m.away_team}`
  );

  return (
    <div className="live-page">

      {/* ── Header ── */}
      <div className="live-page-header">
        <div className="live-title-row">
          <span className="live-pulse" />
          <h1 className="page-title">Matchs en Direct</h1>
          {!loading && (
            <span className="live-count-badge">{count} match{count !== 1 ? 's' : ''}</span>
          )}
        </div>
        <p className="page-subtitle">
          Scores mis à jour toutes les 30 secondes
          {lastUpdate && (
            <span className="live-last-update">
              · Dernière mise à jour : {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </p>
      </div>

      {/* ── Ticker ── */}
      {tickerItems.length > 0 && (
        <div className="live-ticker">
          <span className="ticker-label">
            <span className="live-pulse live-pulse-sm" /> EN DIRECT
          </span>
          <div className="ticker-track">
            <div className="ticker-content">
              {[...tickerItems, ...tickerItems].map((t, i) => (
                <span key={i} className="ticker-item">{t}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── League filter pills ── */}
      <div className="category-scroll">
        <button
          className={`category-pill ${activeLeague === null ? 'active' : ''}`}
          onClick={() => setActiveLeague(null)}
        >
          Toutes
        </button>
        {leagues.map((lg) => (
          <button
            key={lg.league}
            className={`category-pill ${activeLeague === lg.league ? 'active' : ''}`}
            onClick={() => setActiveLeague(lg.league)}
          >
            {lg.league_logo && (
              <img src={lg.league_logo} alt={lg.league}
                width={14} height={14} className="pill-logo"
                onError={(e) => { e.target.style.display = 'none'; }} />
            )}
            {lg.league}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {loading && <LiveGridSkeleton count={4} />}

      {/* ── Error ── */}
      {error && !loading && (
        <div className="live-error">
          Impossible de charger les matchs : {error}
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && !error && fixtures.length === 0 && (
        <div className="live-empty">
          <div className="live-empty-icon">⚽</div>
          <p className="live-empty-title">Aucun match en direct pour le moment</p>
          <p className="live-empty-sub">Revenez pendant la World Cup ou lors des matchs du soir !</p>
          <div className="live-empty-refresh">
            <span className="live-pulse live-pulse-sm" />
            Vérification automatique toutes les 30 secondes
          </div>
        </div>
      )}

      {/* ── Match grid ── */}
      {!loading && fixtures.length > 0 && (
        <div className="live-matches-grid">
          {fixtures.map((match) => (
            <LiveMatchCard key={match.id} match={match} />
          ))}
        </div>
      )}

      {/* ── Info banner ── */}
      {/* {!loading && (
        <div className="live-info-banner">
          <div className="lib-item">
            <FaChartBar className="lib-icon" />
            <div>
              <p className="lib-title">Statistiques en direct</p>
              <p className="lib-desc">Scores mis à jour toutes les 30 secondes via le scheduler</p>
            </div>
          </div>
          <div className="lib-item">
            <FaBolt className="lib-icon" />
            <div>
              <p className="lib-title">Cotes verrouillées</p>
              <p className="lib-desc">Les paris se placent avant le coup d'envoi sur la page Sports</p>
            </div>
          </div>
        </div>
      )} */}

    </div>
  );
}
